import { useCallback, useRef, useState } from "react";
import type { TaskStatus } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { BoardCardMenu } from "@design-system/Board/BoardCardMenu";
import { BoardCardTitleButton } from "@design-system/Board/BoardCardTitleButton";
import { Menu } from "@design-system/Menu/Menu.lib";
import { OverlayDeferralContext } from "@design-system/overlay/overlayDeferral";
import { VIEW_ONLY_WRITE_REFUSAL_MESSAGE } from "@common/userWorkspaceRoles";
import { AgentRunActivityDrawer } from "@components/presence/AgentRunActivityDrawer";
import { useAgentRunForTask } from "@components/presence/useAgentRunForTask";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTerminatingStatusGuard } from "@hooks/useTerminatingStatusGuard";
import { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import { TaskActionMenuItems, useTaskActions } from "../useTaskActions";
import type { TaskBoardCardProps } from "./TaskBoardCard";
import {
    getConfirmingEditor,
    getTaskCardChipFields,
    TaskStatusChip,
} from "./TaskBoardCardChips";
import { useUnofferedTaskFields } from "./TaskBoardCloseControls";
import type { TaskFieldEditor } from "./TaskBoardFieldControls";
import { TaskBoardShortcutAnchor } from "./TaskBoardShortcutAnchor";
import {
    taskControlProps,
    taskFieldProps,
    useOpenTaskFieldChip,
} from "./TaskChipButton";
import { isTaskFieldEditable, type TaskCardEditField } from "./taskCardEdits";
import { getTaskPresenceField } from "./TaskCardPresence";
import type { TaskPresence } from "./taskPresenceMembers";
import { withStatusField } from "./taskBoardFields";
import { TaskTitleEditor } from "./TaskTitleEditor";
import { useTaskCardArming } from "./useTaskCardArming";
import styles from "./TaskBoardChips.module.scss";

type TaskBoardCardBodyProps = TaskBoardCardProps & {
    /** The card's read-only fields, which the editable ones become chips of. */
    fields: readonly BoardCardField[];
    presence: TaskPresence;
    /** The card's accessible name (`useTaskCardLabel`). */
    label: string;
};

/**
 * An editable task card: the status chip and the visible editable fields are
 * chips that open List's controls, the Add field chip opens an empty one's,
 * and a shortcut opens a field's control over the card (toasting a save the
 * card doesn't show, as Add field does). With inline renaming,
 * clicking the title
 * text renames it in place (a plain title for View-only users); otherwise R
 * renames in the title modal. Finishing a blocked task asks first, as List's
 * status control does. The kebab and a right-click open the same task
 * actions. The chips' controls mount once the card is first reached
 * (`useTaskCardArming`).
 */
export const TaskBoardCardBody = ({
    task,
    hiddenFields,
    fields,
    presence,
    label,
    onOpen,
    onOpenChat,
    onEditDetails,
    onEdit,
    renameMode,
    shortcut,
    onShortcutEnd,
}: Readonly<TaskBoardCardBodyProps>) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const { isArmed, armProps } = useTaskCardArming({
        cardRef,
        isTargeted: shortcut !== null,
    });
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [addingField, setAddingField] = useState<TaskCardEditField | null>(
        null,
    );
    const openChipField = useOpenTaskFieldChip(cardRef);
    const unofferedFields = useUnofferedTaskFields();
    const { isViewOnly } = useViewOnlyRefusal();
    const updateStatus = useCallback(
        (status: TaskStatus) => {
            onEdit({ task, body: { status } });
            return Promise.resolve();
        },
        [onEdit, task],
    );
    const guard = useTerminatingStatusGuard(task, updateStatus);
    const editor: TaskFieldEditor = {
        task,
        onEdit,
        onStatusChange: guard.handleStatusChange,
    };
    const focusCard = () => cardRef.current?.focus({ preventScroll: true });
    const canRenameInline = renameMode === "inline" && !isViewOnly;
    const { run, isWorking: isAgentWorking } = useAgentRunForTask(task.id);
    const { items: actionItems, dialogs: actionDialogs } = useTaskActions({
        task,
        onEditDetails,
        open: { onOpen, onOpenChat },
        editDisabledReason: isViewOnly
            ? VIEW_ONLY_WRITE_REFUSAL_MESSAGE
            : undefined,
        renameRequest:
            shortcut?.field === "title"
                ? {
                      onClose: () => {
                          onShortcutEnd(shortcut.id);
                          focusCard();
                      },
                  }
                : undefined,
    });
    const canAssign =
        !isViewOnly && isTaskFieldEditable({ task, field: "assignee" });
    const cardFields = [
        ...withStatusField({
            fields,
            status: !hiddenFields.has(TaskColumn.STATUS) && (
                <TaskStatusChip editor={editor} />
            ),
        }),
        getTaskPresenceField({
            ...presence,
            isAgentWorking,
            editor: canAssign ? editor : undefined,
        }),
    ];

    const getTitle = () => {
        if (isEditingTitle) {
            return (
                <TaskTitleEditor
                    initialTitle={task.title}
                    onRename={(title) => onEdit({ task, body: { title } })}
                    onEnd={() => setIsEditingTitle(false)}
                    onReturnFocus={focusCard}
                />
            );
        }
        return canRenameInline ? (
            <BoardCardTitleButton
                {...taskFieldProps("title")}
                onClick={() => setIsEditingTitle(true)}
            >
                {task.title}
            </BoardCardTitleButton>
        ) : (
            task.title
        );
    };

    return (
        <>
            <Menu.Context
                trigger={
                    <BoardCard
                        {...armProps}
                        ref={cardRef}
                        aria-label={label}
                        onClick={() => onOpen(task)}
                        className={styles.taskCard}
                        drawer={
                            run ? (
                                <AgentRunActivityDrawer run={run} />
                            ) : undefined
                        }
                    >
                        <OverlayDeferralContext.Provider value={!isArmed}>
                            <BoardCardLayout
                                title={getTitle()}
                                menu={
                                    <BoardCardMenu
                                        triggerAttributes={taskControlProps(
                                            "actions",
                                        )}
                                    >
                                        <TaskActionMenuItems
                                            items={actionItems}
                                        />
                                    </BoardCardMenu>
                                }
                                fields={getTaskCardChipFields({
                                    fields: cardFields,
                                    editor,
                                    adding: {
                                        field: addingField,
                                        onFieldChange: setAddingField,
                                    },
                                    openField: openChipField,
                                    axisFields: hiddenFields,
                                    unofferedFields,
                                })}
                            />
                            {shortcut !== null &&
                                shortcut.field !== "title" && (
                                    <TaskBoardShortcutAnchor
                                        key={shortcut.id}
                                        field={shortcut.field}
                                        editor={getConfirmingEditor({
                                            editor,
                                            field: shortcut.field,
                                            fields: cardFields,
                                            axisFields: hiddenFields,
                                        })}
                                        onEnd={() => onShortcutEnd(shortcut.id)}
                                    />
                                )}
                        </OverlayDeferralContext.Provider>
                    </BoardCard>
                }
            >
                <TaskActionMenuItems items={actionItems} />
            </Menu.Context>
            {actionDialogs}
            {isArmed && (
                <TaskStatusTerminationConfirmationModal
                    pendingStatus={guard.pendingStatus}
                    activeBlockerCount={guard.activeBlockerCount}
                    onConfirm={guard.confirmChange}
                    onCancel={guard.cancelChange}
                    showDontAskAgain={guard.showDontAskAgain}
                    shouldSkipConfirmation={guard.shouldSkipConfirmation}
                    onShouldSkipConfirmationChange={
                        guard.setShouldSkipConfirmation
                    }
                />
            )}
        </>
    );
};
